import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { SituViewWebApiClient, Product2, Company, PropertyType, AssetTemplateProperties, GetAssetInstanceRequest, GetAssetTemplateRequest } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, Utils } from 'src/app/services';
import { AssetInstance, AssetInstanceValueTypes, AssetPropertyDetails, AssetReferenceValue, AssetTemplate, AssetTemplateProperty, INewProduct, KeyValuePair } from 'src/app/views';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'artifact-tree',
  templateUrl: './artifact-tree.component.html',
  styleUrls: ['./artifact-tree.component.less']
})
export class ArtifactTreeComponent extends SituBaseComponent {
  
  @Input() product: INewProduct = new Product2();
  @Input() productCompany?: Company;
  @Input() assetInstanceKey?: string;
  @Input() assetTemplateKey?: string;
  @Input() assetInstance?: AssetInstance;
  @Input() assetInstances: Record<string, AssetInstance> = {};
  @Input() assetTemplates: Record<string, AssetTemplate> = {};
  @Input() assetDepth: number = 0;
  @Output() findAndReplaceAssetReference: EventEmitter<AssetPropertyDetails | undefined> = new EventEmitter();
  @Output() propertyEdited: EventEmitter<AssetPropertyDetails | undefined> = new EventEmitter();

  openAssetInstances: AssetInstanceValueTypes[] = [];

  assetTemplate?: AssetTemplate;
  
  propertyType = PropertyType;

  showAllProperties = true; // this is set to false if the template type is in visiblePropertiesDefaults
  visiblePropertiesDefaults: Record<string, Set<string>> = {
    material: new Set<string>(['Albedo', 'Normal Map', 'Metallic Map', 'Manufacturer', 'SKU', 'Name', 'Notes', 'Offset X', 'Offset Y', 'Tiling X', 'Tiling Y', 'RenderingMode']),
    product: new Set<string>(['Mesh', 'Materials', 'Manufacturer', 'SKU', 'ProductName', 'Notes']),
  }

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewClient: SituViewWebApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar
  ) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.assetInstanceKey && !changes.assetInstanceKey.firstChange) {
      // The caller refernce key has been changed, load the new artifact details
      this.load();
    }
  }

  async load() {
    if (!this.assetTemplateKey && this.assetInstanceKey) {
      var splitKey = this.assetInstanceKey.split('\\');
      this.assetTemplateKey = `assettemplate\\situsystems\\${splitKey[2]}`;
    }
    
    if (!this.assetInstance) {
      await this.getAssetInstance();
    }

    if (!this.assetTemplate) {
      await this.getAssetTemplate();
    }

    var templateNameLowerCase = this.assetInstance!.templateName.toLowerCase();
    // Hide the less used properties, if this template type is set to do so.
    this.showAllProperties = !(templateNameLowerCase in this.visiblePropertiesDefaults);

    if (this.assetInstance && this.assetTemplate) {
      
      for (let property of this.assetTemplate.properties) {

        // Add the default value for this property if it does not exist
        if (!(property.propertyName! in this.assetInstance.values) ||
            this.assetInstance.values[property.propertyName!] === undefined){
          this.assetInstance.values[property.propertyName!] = property.defaultValue;
        }

        var value = this.assetInstance.values[property.propertyName!];

        if (property.assetType === PropertyType.TEMPLATE || 
          property.assetType === PropertyType.MESH ||
          property.assetType === PropertyType.IMAGE || 
          property.assetType === PropertyType.BLENDER
        ) {
          var boxedValue = ArtifactTreeComponent.boxCallerReferenceKeyString(value);
          if(boxedValue){
            this.assetInstance.values[property.propertyName!] = boxedValue;
          }
        }

        if (!this.showAllProperties) {
          // If some properties are being hidden, make sure any set artifact references are visable by default.
          var visiblePropertyDefaults = this.visiblePropertiesDefaults[templateNameLowerCase];
          if (property.assetType === PropertyType.TEMPLATE &&
            value && typeof value === 'object' && 'callerReferenceKey' in value &&
            value.callerReferenceKey && value.callerReferenceKey != 'NA') {
            visiblePropertyDefaults.add(property.propertyName!);
          }
        }

      }

      //console.log(this.assetInstance);
    }
  }

  static boxCallerReferenceKeyString(value: AssetInstanceValueTypes, key?: string) : AssetInstanceValueTypes  {
    
    if (value === undefined || value === null) {
      return new AssetReferenceValue();
    }

    
    if (typeof value === 'string') {
        var assetInstanceValue = new AssetReferenceValue();
        assetInstanceValue.callerReferenceKey = value;
        assetInstanceValue.name = key;
        value = assetInstanceValue;
    } else if (Array.isArray(value)) {
      var splitKey = key?.match(/\((.*?)\)/)?.[1]?.split(",");
      for (let i = 0; i < value.length; i++) {
        var boxedItem = this.boxCallerReferenceKeyString(value[i], splitKey?.[i]);
        if (boxedItem) {
          value[i] = boxedItem;
        }
      } 
    } else if (typeof value === 'object' && 'key' in value && 'value' in value) {
      // This is a key value pair
      value.value = this.boxCallerReferenceKeyString(value.value, value.key);
    }

    return value;
  }

  async getAssetInstance() {
    if (!this.assetInstanceKey || !this.assetTemplateKey) {
      return;
    }

    // See if it has already been loaded
    var assetInstance = this.assetInstances[this.assetInstanceKey];
    if (assetInstance) {
      this.assetInstance = assetInstance;
      return;
    }
    
    this.loading++;

    try {
      var request = new GetAssetInstanceRequest();
      request.companyId = this.productCompany?.id;
      request.assetInstanceKey = this.assetInstanceKey;
      var result  = await this.situViewClient.getAssetInstance(request);

      if (!result.success) {
        this.showMessage("Failed to load product asset infomation")
        return;
      }

      assetInstance = result.assetInstance as AssetInstance;

      // Some Asset Instances don't have templateID set, but we make heave use of it in the portal
      if (!assetInstance.templateID) {
        assetInstance.templateID = this.assetTemplateKey;
      }

      this.assetInstances[this.assetInstanceKey] = assetInstance;
      this.assetInstance = assetInstance;

      this.assetTemplate = { properties: result.assetTemplate?.map(x => x as AssetTemplateProperty) } as AssetTemplate;
      this.assetTemplates[this.assetTemplateKey] = this.assetTemplate;

      //console.log(this.assetInstance);
    } finally {
      this.loading--;
    }
  }

  async getAssetTemplate() {
    if (!this.assetTemplateKey) {
      return;
    }

    // See if it has already been loaded
    var assetTemplate = this.assetTemplates[this.assetTemplateKey];
    if (assetTemplate) {
      this.assetTemplate = assetTemplate;
      return;
    }
    
    this.loading++;

    try {
      var request = new GetAssetTemplateRequest();
      request.assetTemplateKey = this.assetTemplateKey;
      var result  = await this.situViewClient.getAssetTemplate(request);

      if (!result.success) {
        this.showMessage("Failed to load product asset infomation")
        return;
      }

      assetTemplate = { properties: result.assetTemplate?.map(x => x as AssetTemplateProperty) } as AssetTemplate;
      this.assetTemplate = assetTemplate
      this.assetTemplates[this.assetTemplateKey] = assetTemplate;

      //console.log(this.assetTemplate);
    } finally {
      this.loading--;
    }
  }

  onPropertyEdited(templateProperty: AssetTemplateProperty, assetProperty: AssetReferenceValue ) {
    var property = new AssetPropertyDetails;
    property.assetProperty = assetProperty;
    property.assetPropertyTemplate = templateProperty;

    property.assetInstanceKey = this.assetInstanceKey!;
    property.assetTemplate = this.assetTemplate!;
    property.assetInstance = this.assetInstance!;

    this.propertyEdited.emit(property);
  }

  toggleShowAssetInstance(assetProperty: AssetInstanceValueTypes) {
    const index = this.openAssetInstances.indexOf(assetProperty);
    if (index > -1) {
      this.openAssetInstances.splice(index, 1);
    } else {
      this.openAssetInstances.push(assetProperty);
    }
  }

  shouldShowAssetInstance(assetProperty: AssetInstanceValueTypes): boolean {
    if (this.canShowAssetInstance(assetProperty)) {
      return this.openAssetInstances.includes(assetProperty);
    }

    return false;
  }

  canShowAssetInstance(assetProperty: AssetInstanceValueTypes): boolean {
    if (typeof assetProperty === 'object' && 'callerReferenceKey' in assetProperty) {
      return !!assetProperty.callerReferenceKey && assetProperty.callerReferenceKey.startsWith("assetinstance");
    }

    if (typeof assetProperty === 'object') {
      return true;
    }

    return false;
  }

  getEnumOptions(enumValues: string): string[] {
    return enumValues ? enumValues.split(',') : [];
  }

  propertyTypeName(templateProperty: AssetTemplateProperty): string {
    var name = "";

    if (templateProperty.templateName) {
      name = `${templateProperty.templateName} `;
    }

    name += Utils.capitalizeFirstLetter(PropertyType[templateProperty.assetType!].toLowerCase());

    if (templateProperty.assetCount! > 1) {
      name += ' (Collection)';
    }

    return name;
  }

  showProperty(templateProperty: AssetTemplateProperty): boolean {
    if (this.showAllProperties) {
      return true;
    }

    var assetTemplateName = this.assetInstance!.templateName.toLowerCase();
    if (assetTemplateName in this.visiblePropertiesDefaults){
      var templateVisableDefaults = this.visiblePropertiesDefaults[assetTemplateName];
      if (templateVisableDefaults) {
        return templateVisableDefaults.has(templateProperty.propertyName!);
      } 
    }

    return true;
  }

  getObjectKeys(value: any): string[] {
    if (!value) {
      return [];
    }

    var collection = Object.keys(value)
    return collection;
  }

  clearAsset(assetProperty: AssetReferenceValue ) {
    this.clearObject(assetProperty);

    assetProperty.callerReferenceKey = undefined;
  }

  clearObject(obj: Record<string, any>): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        delete obj[key];
      }
    }
  }
  
  onFindAndReplaceAssetReference(templateProperty: AssetTemplateProperty | undefined, assetProperty: AssetReferenceValue ) {
    if (!templateProperty) {
      this.findAndReplaceAssetReference.emit(undefined);
      return;
    }

    var property = new AssetPropertyDetails;
    property.assetProperty = assetProperty;
    property.assetPropertyTemplate = templateProperty;

    property.assetInstanceKey = this.assetInstanceKey!;
    property.assetTemplate = this.assetTemplate!;
    property.assetInstance = this.assetInstance!;

    this.findAndReplaceAssetReference.emit(property);
  }

  addNewItemToCollection(assetProperty: AssetInstanceValueTypes, propertyName: string) {
    if (!this.assetInstance!.values[propertyName]) {
      assetProperty = new Array<AssetReferenceValue>();
      this.assetInstance!.values[propertyName] = assetProperty;
    }

    if (Array.isArray(assetProperty)) {
      var newValue = new AssetReferenceValue();
      assetProperty.push(newValue);
    }
  }

  removeItemFromCollection(assetProperty: AssetInstanceValueTypes) {
    if (Array.isArray(assetProperty)) {
      assetProperty.pop();
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  isArray(item: any): boolean {
    return Array.isArray(item);
  }

  blockArrowKeys(e: KeyboardEvent) {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  }
}